/**
 * Расшифровка событий ЮНИГАЗЕТЫ для лога DC.
 *
 * События материалов размазаны по категориям nav и commerce (нажатие на рубрику —
 * навигация, досмотр ролика — коммерция), поэтому «Газета» это не category, а сквозной
 * признак: экран tab:materials либо kind, начинающийся на vestnik.
 *
 * В логе такие события выглядели сырым дампом («video_watch: session_detail»), хотя
 * в meta лежит всё нужное: секунды, глубина скролла, номинация, сумма. Здесь meta
 * разворачивается в живую фразу.
 */

export interface VestnikEventLike {
  kind: string;
  screen: string;
  label: string;
  refId: string;
  meta: unknown;
}

/** Признак «событие газеты» — по экрану или по префиксу kind. */
export function isVestnikEvent(e: { kind: string; screen: string }): boolean {
  return e.screen === "tab:materials" || e.kind.startsWith("vestnik_");
}

function metaObj(meta: unknown): Record<string, unknown> {
  if (meta && typeof meta === "object") return meta as Record<string, unknown>;
  if (typeof meta === "string") {
    try { const p: unknown = JSON.parse(meta); return p && typeof p === "object" ? p as Record<string, unknown> : {}; } catch { return {}; }
  }
  return {};
}
function num(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}
/** Секунды → «1 мин 12 сек» / «47 сек». Дробные хвосты в логе не нужны. */
function secs(v: unknown): string {
  const total = Math.round(num(v));
  if (total < 60) return `${total} сек`;
  return `${Math.floor(total / 60)} мин ${total % 60} сек`;
}
const q = (s: string): string => (s ? ` «${s}»` : "");

/**
 * Событие газеты → фраза для лога. null, если событие не про газету —
 * тогда вызывающий откатывается на общее описание.
 */
export function describeVestnik(e: VestnikEventLike): string | null {
  if (!isVestnikEvent(e)) return null;
  const m = metaObj(e.meta);
  const uc = num(m.amount_uc);
  const ucTail = uc ? ` (${uc > 0 ? "+" : ""}${Math.round(uc * 100) / 100} UC)` : "";

  switch (e.kind) {
    case "view": return "Открыл ЮНИГАЗЕТУ";
    case "tap": return e.label ? `Нажал${q(e.label)}` : "Нажал кнопку в газете";

    case "vestnik_dwell": return `Читал газету ${secs(m.total_seconds ?? m.seconds)}`;
    case "vestnik_scroll_depth": return `Долистал до ${Math.round(num(m.depth))}%`;
    case "vestnik_rubric_view": return `Дошёл до рубрики${q(e.label)}`;
    case "vestnik_editor_read": return "Дочитал колонку главреда";

    case "vestnik_horoscope_tap": return `Открыл гороскоп${q(e.label)}`;
    case "vestnik_horoscope_reward_tap": return `Тянется за юникоинами гороскопа${q(e.label)}`;
    case "vestnik_horoscope_reward_claim": return `Забрал юникоины гороскопа${q(e.label)}${ucTail}`;

    case "vestnik_interview_play": return "Включил интервью";
    case "vestnik_interview_watch": {
      const done = m.completed === true ? ", досмотрел до конца" : "";
      return `Смотрел интервью ${secs(m.watched_sec)} из ${secs(m.duration_sec)}${done}`;
    }

    case "vestnik_yuskar_nomination_view": return `Изучает номинацию${q(e.label)}`;
    case "vestnik_yuskar_video_play": return `Включил ролик номинанта${q(e.label)}`;
    case "vestnik_yuskar_vote_tap": return `Примеряется голосовать${q(e.label)}`;
    case "vestnik_yuskar_vote":
      return m.changed === true
        ? `ПЕРЕГОЛОСОВАЛ в номинации${q(e.label)} → ${e.refId || "?"}`
        : `Проголосовал в номинации${q(e.label)} → ${e.refId || "?"}`;
    case "vestnik_yuskar_results_view": return "Смотрит расклад голосования";
    case "vestnik_yuskar_submission_open": return "Открыл отправку своего видео в ЮСКАР";

    case "vestnik_player_video_play": return `Включил ролик игрока${q(e.label)}`;
    case "vestnik_player_video_switch": return `Переключился на ролик${q(e.label)}`;
    case "vestnik_player_video_watch": {
      const done = m.completed === true ? ", досмотрел" : "";
      return `Смотрел ролик игрока${q(e.label)} ${secs(m.watched_sec)}${done}`;
    }
    case "vestnik_player_recommendation_impression": return `Увидел рекомендацию${q(e.label)}`;
    case "vestnik_player_tip":
    case "montage_tip": return `Задонатил автору монтажа${q(e.label)}${ucTail}`;
    case "vestnik_player_montage_submit_tap": return "Пошёл отправлять свой монтаж";

    default: return null;
  }
}
